//
// Pure data mapping for the User-scoped notification detail hydration path.
// Deliberately imports nothing from gl-commonui/service.ts — see the task note
// on why this needs to be independently loadable.
export interface HydratedUser {
    id: string;
    name?: string;
    email?: string;
}

export function buildUserTargetName(user: { name?: string; email?: string; tenantId?: number | null }): string {
    return JSON.stringify({
        name: user.name,
        email: user.email,
        tenantId: user.tenantId,
    });
}

// Every target hydrated here belongs to the SAME single notification, hence the SAME single
// owner tenant — the Account API's own per-user `tenantId` is NOT a reliable source (a user
// can belong to multiple tenants, so Account may legitimately return `null`/a different
// tenant for a user who is, in fact, this notification's own target). So `ownerTenantId`
// (resolved from the notification's own owner context by the caller) is applied to EVERY
// target here, found-in-Account or not — never the Account lookup's own tenantId.
export function hydrateUserTargets(
    userIds: string[],
    users: HydratedUser[],
    roleCode: number,
    targetTypeCode: number,
    ownerTenantId: number | null | undefined
): { role: number; targetType: number; targetId: string; targetName: string }[] {
    const byId = new Map<string, HydratedUser>();
    users.forEach((u) => byId.set(u.id, u));
    return userIds.map((uid) => {
        const u = byId.get(uid) || { id: uid, name: "", email: "" };
        return {
            role: roleCode,
            targetType: targetTypeCode,
            targetId: u.id,
            targetName: buildUserTargetName({ name: u.name, email: u.email, tenantId: ownerTenantId }),
        };
    });
}
